import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Icon } from 'semantic-ui-react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import * as Styled from './applyCard_styled';
import { postQuickRegister } from '../../../../../../api/api';
import { extractUTM } from '../../../../../../helperFunctions/helperFunctions';
import { saveAuthenticationData } from '../../../../../../actions/authentication_actions';
import { openTimeoutMessageModal } from '../../../../../../actions/modals_actions';

function parseJwt(token) {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (error) {
    return {};
  }
}

const REDIRECT_TIMEOUT_MS = 3000;

const ApplyCard = ({ jobId, companySlug, showCompanyLogo }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation('jobDescriptionQuickApply');

  const [email, setEmail] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [errors, setErrors] = useState(null);
  const [showCard, setShowCard] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    document.head.appendChild(script);

    window.onTurnstileSuccess = (token) => {
      if (isMounted) setTurnstileToken(token);
    };

    return () => {
      isMounted = false;
      document.head.removeChild(script);
      delete window.onTurnstileSuccess;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      setErrors('Please enter your email.');
      return;
    }

    if (!turnstileToken) {
      setErrors('Please complete the captcha.');
      return;
    }

    if (!acceptedTerms) {
      setErrors('Please accept the terms of use.');
      return;
    }

    setIsSubmitting(true);

    try {
      const utmData = extractUTM();
      const response = await postQuickRegister(
        window.activeEnvironment,
        email,
        jobId,
        utmData,
      );
      const { data } = response;

      if (data.token && data.magic_link_sent === false) {
        const jwtPayload = parseJwt(data.token);
        const applicantId = jwtPayload.id;
        const authenticationData = {
          expire: true,
          owner: 'apploi',
          value: { id: applicantId },
          token: data.token,
        };
        localStorage.setItem(
          'token',
          JSON.stringify({ expire: true, owner: 'apploi', value: data.token }),
        );
        localStorage.setItem('applicant', JSON.stringify(authenticationData));
        await dispatch(saveAuthenticationData(authenticationData, 'applicant'));
        history.push(`/view/${jobId}#jobDescriptionFullApplyFormScroll`);
        setShowCard(false);
      } else if (data.magic_link_sent === true) {
        setShowConfirmation(true);
      } else {
        setErrors('Unexpected response format');
        throw new Error('Unexpected response format');
      }
    } catch (err) {
      // Check if this is a 409 conflict error (duplicate application)
      if (err?.response?.status === 409) {
        // For blind ads, show message without redirect text
        /* eslint-disable operator-linebreak */
        const alreadyAppliedMessage = showCompanyLogo
          ? t('alreadyAppliedMessage')
          : t('alreadyApplied');
        /* eslint-enable operator-linebreak */

        dispatch(
          openTimeoutMessageModal({
            headerMessage: t('alreadyApplied'),
            bodyMessage: alreadyAppliedMessage,
            footerMessage: null,
            timeout: REDIRECT_TIMEOUT_MS,
            pageReload: false,
          }),
        );

        // Redirect to company profile after showing the message (only for non-blind ads)
        setTimeout(() => {
          if (showCompanyLogo && companySlug) {
            history.push(`/profile/${companySlug}`);
          }
        }, REDIRECT_TIMEOUT_MS);
      } else {
        // For non-409 errors, use optional chaining to safely access error properties
        setErrors(
          err?.response?.data?.user_message ||
            err?.message ||
            'Failed to submit application',
        );
      }
    } finally {
      setIsSubmitting(false);
      if (window.turnstile) {
        window.turnstile.reset();
      }
    }
  };

  if (!showCard) {
    return null;
  }

  return (
    <React.Fragment>
      <Styled.CardContainer>
        <Styled.Title>Apply Now</Styled.Title>
        <form onSubmit={handleSubmit}>
          <Styled.EmailInput
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <Styled.CaptchaContainer>
            <div
              className="cf-turnstile"
              data-sitekey={window.turnstileSiteKey}
              data-callback="onTurnstileSuccess"
            />
          </Styled.CaptchaContainer>
          <Styled.TermsCheckboxContainer>
            <input
              type="checkbox"
              id="termsCheckbox"
              aria-describedby="termsDescription"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              required
            />
            <label htmlFor="termsCheckbox" id="termsDescription">
              By checking this box, I agree to the{' '}
              <a
                href="https://apploi.com/terms-of-use/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Use
              </a>
              ,{' '}
              <a
                href="https://www.apploi.com/cookie-policy/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Cookies
              </a>{' '}
              and{' '}
              <a
                href="https://www.apploi.com/privacy_policy/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy.
              </a>
            </label>
          </Styled.TermsCheckboxContainer>
          <Styled.SubmitButton
            type="submit"
            disabled={
              !acceptedTerms || !email || !turnstileToken || isSubmitting
            }
          >
            Continue
          </Styled.SubmitButton>
          {errors && (
            <div style={{ color: 'red', marginTop: '10px' }}>{errors}</div>
          )}
        </form>
      </Styled.CardContainer>
      {showConfirmation &&
        ReactDOM.createPortal(
          <Styled.ConfirmationModal>
            <Styled.ModalContent>
              <Styled.CheckIconContainer>
                <Icon name="check" size="large" />
              </Styled.CheckIconContainer>
              <Styled.ModalTitle>Check Your Email</Styled.ModalTitle>
              <Styled.ModalText>
                A link has been sent to <b>{email}</b>. Please check the link to
                begin the apply process.
              </Styled.ModalText>
              <Styled.ModalButton
                onClick={() => {
                  setShowConfirmation(false);
                  if (companySlug) {
                    history.push(`/profile/${companySlug}`);
                  }
                }}
              >
                OK
              </Styled.ModalButton>
            </Styled.ModalContent>
          </Styled.ConfirmationModal>,
          document.body,
        )}
    </React.Fragment>
  );
};

ApplyCard.propTypes = {
  jobId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  companySlug: PropTypes.string,
  showCompanyLogo: PropTypes.bool,
};

ApplyCard.defaultProps = {
  companySlug: '',
  showCompanyLogo: true,
};

export default ApplyCard;
